import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { useCurrency } from "@/context/CurrencyContext";
import { DashboardLayout } from "@/components/DashboardLayout";
import { StatCard } from "@/components/Bits";
import { LANGUAGES } from "@/i18n";
import { Send, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

export default function Account() {
  const { user, refresh } = useAuth();
  const { t, lang, setLang } = useLang();
  const { currency, setCurrency, supported, updatedAt } = useCurrency();
  const [prefs, setPrefs] = useState(null);
  const [chatId, setChatId] = useState("");
  const [sub, setSub] = useState(null);

  useEffect(() => {
    api.get("/settings/preferences").then((r) => setPrefs(r.data)).catch(() => {});
    api.get("/billing/subscription").then((r) => setSub(r.data)).catch(() => {});
  }, []);

  const savePref = async (patch) => {
    const { data } = await api.put("/settings/preferences", patch);
    setPrefs(data); toast.success(t("common.saved"));
    refresh();
  };

  const connectTelegram = async () => {
    if (!chatId.trim()) return;
    try {
      await api.post("/settings/telegram/connect", { chat_id: chatId.trim() });
      toast.success("Telegram connected — check for a test message");
      setChatId(""); api.get("/settings/preferences").then((r) => setPrefs(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not connect Telegram"); }
  };
  const disableTelegram = async () => { await api.post("/settings/telegram/disable"); api.get("/settings/preferences").then((r) => setPrefs(r.data)); };

  const openPortal = async () => {
    try {
      const { data } = await api.post("/billing/portal", { return_url: window.location.href });
      window.location.href = data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Unable to open billing portal"); }
  };
  const cancelSub = async () => {
    try { await api.post("/billing/cancel"); toast.success("Subscription will cancel at period end"); api.get("/billing/subscription").then((r) => setSub(r.data)); }
    catch (e) { toast.error(e.response?.data?.detail || "Cancel failed"); }
  };
  const resumeSub = async () => {
    try { await api.post("/billing/resume"); toast.success("Subscription resumed"); api.get("/billing/subscription").then((r) => setSub(r.data)); }
    catch (e) { toast.error(e.response?.data?.detail || "Resume failed"); }
  };

  const Toggle = ({ on, onClick, testid }) => (
    <button onClick={onClick} data-testid={testid} className={`h-6 w-11 rounded-full p-0.5 transition-colors ${on ? "bg-primary" : "bg-secondary"}`}>
      <span className={`block h-5 w-5 rounded-full bg-white transition-transform ${on ? "translate-x-5" : ""}`} />
    </button>
  );

  return (
    <DashboardLayout title={t("nav.settings")}>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="lg:col-span-2 space-y-6">
          {/* Profile */}
          <div className="rounded-2xl border border-border bg-card p-6">
            <h3 className="font-display text-lg font-semibold">{t("settings.profile")}</h3>
            <div className="mt-4 grid grid-cols-2 gap-4 text-sm">
              <Info label={t("auth.name")} value={user?.name} />
              <Info label={t("auth.email")} value={user?.email} />
              <Info label="Role" value={user?.role} />
              <Info label="Experience" value={user?.experience || "—"} />
              <Info label="Risk preference" value={user?.risk_preference || "—"} />
              <Info label="Referral code" value={user?.referral_code} />
            </div>
          </div>

          {/* Preferences */}
          <div className="rounded-2xl border border-border bg-card p-6">
            <h3 className="font-display text-lg font-semibold">{t("settings.preferences")}</h3>
            <div className="mt-4 grid gap-4 sm:grid-cols-2">
              <div>
                <label className="mb-1.5 block text-sm font-medium">{t("common.language")}</label>
                <select value={lang} onChange={(e) => { setLang(e.target.value); }} data-testid="settings-language" className="w-full rounded-xl border border-border bg-card px-3 py-2.5 text-sm">
                  {LANGUAGES.map((l) => <option key={l.code} value={l.code}>{l.flag} {l.label}</option>)}
                </select>
              </div>
              <div>
                <label className="mb-1.5 block text-sm font-medium">{t("common.currency")}</label>
                <select value={currency} onChange={(e) => setCurrency(e.target.value)} data-testid="settings-currency" className="w-full rounded-xl border border-border bg-card px-3 py-2.5 text-sm">
                  {supported.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
                {updatedAt && <p className="mt-1 text-[10px] text-muted-foreground">FX updated {new Date(updatedAt).toLocaleString()}</p>}
              </div>
            </div>
          </div>

          {/* Notifications */}
          {prefs && (
            <div className="rounded-2xl border border-border bg-card p-6">
              <h3 className="font-display text-lg font-semibold">{t("settings.notifications")}</h3>
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between">
                  <div><div className="text-sm font-medium">{t("settings.emailAlerts")}</div><div className="text-xs text-muted-foreground">{prefs.notifications.email ? "Email provider connected" : "Email provider not configured"}</div></div>
                  <Toggle on={prefs.email_alerts} onClick={() => savePref({ email_alerts: !prefs.email_alerts })} testid="toggle-email-alerts" />
                </div>
                <div className="flex items-center justify-between">
                  <div><div className="text-sm font-medium">{t("settings.weeklyBrief")}</div><div className="text-xs text-muted-foreground">Generated from real market data, once per week</div></div>
                  <Toggle on={prefs.weekly_brief} onClick={() => savePref({ weekly_brief: !prefs.weekly_brief })} testid="toggle-weekly-brief" />
                </div>
              </div>

              {/* Telegram */}
              <div className="mt-6 rounded-xl border border-border p-4">
                <div className="flex items-center gap-2"><Send className="h-4 w-4 text-accent" /><span className="text-sm font-semibold">{t("settings.telegram")}</span>
                  {prefs.telegram_connected && <span className="flex items-center gap-1 text-xs text-bullish"><CheckCircle2 className="h-3.5 w-3.5" /> {t("settings.telegramConnected")}</span>}
                </div>
                {!prefs.notifications.telegram ? (
                  <p className="mt-2 text-xs text-warning">Telegram is not configured on the server yet.</p>
                ) : prefs.telegram_connected ? (
                  <button onClick={disableTelegram} data-testid="telegram-disable" className="mt-3 rounded-lg border border-border px-3 py-2 text-xs">{t("common.disable")}</button>
                ) : (
                  <div className="mt-3 flex items-center gap-2">
                    <input value={chatId} onChange={(e) => setChatId(e.target.value)} placeholder={t("settings.telegramChatId")} data-testid="telegram-chatid" className="flex-1 rounded-lg border border-border bg-card px-3 py-2 text-sm" />
                    <button onClick={connectTelegram} data-testid="telegram-connect" className="rounded-lg bg-primary px-3 py-2 text-xs font-semibold text-primary-foreground">{t("settings.telegramConnect")}</button>
                  </div>
                )}
                <p className="mt-2 text-[11px] text-muted-foreground">Start a chat with your bot, then get your numeric chat ID from @userinfobot and paste it here. We'll send a test message to validate.</p>
              </div>
            </div>
          )}
        </div>

        {/* Subscription */}
        <div className="space-y-4">
          <div className="rounded-2xl border border-border bg-card p-6">
            <h3 className="font-display text-lg font-semibold">{t("settings.subscription")}</h3>
            <div className="mt-3"><StatCard label="Current Plan" value={(user?.plan || "free").toUpperCase()} accent="text-primary" testid="account-plan" /></div>
            {sub?.live && (
              <div className="mt-3 space-y-1.5 text-sm" data-testid="billing-live">
                <Row2 label="Status" value={sub.live.status} accent={sub.live.status === "active" || sub.live.status === "trialing" ? "text-bullish" : "text-warning"} />
                {sub.live.amount != null && <Row2 label="Price" value={`${sub.live.currency?.toUpperCase()} ${sub.live.amount}/${sub.live.interval}`} />}
                {sub.live.current_period_end && <Row2 label="Next billing" value={new Date(sub.live.current_period_end * 1000).toLocaleDateString()} />}
                {sub.cancel_at_period_end && <p className="text-xs text-warning">Cancels at period end — access continues until then.</p>}
              </div>
            )}
            {user?.plan === "free" ? (
              <Link to="/pricing" data-testid="account-upgrade" className="mt-4 block w-full rounded-xl bg-primary py-2.5 text-center text-sm font-semibold text-primary-foreground">Upgrade plan</Link>
            ) : sub?.has_customer ? (
              <div className="mt-4 space-y-2">
                <button onClick={openPortal} data-testid="billing-portal" className="w-full rounded-xl bg-primary py-2.5 text-sm font-semibold text-primary-foreground">Manage Subscription</button>
                {sub?.cancel_at_period_end ? (
                  <button onClick={resumeSub} data-testid="billing-resume" className="w-full rounded-xl border border-border py-2.5 text-sm font-semibold">Resume Subscription</button>
                ) : (
                  <button onClick={cancelSub} data-testid="billing-cancel" className="w-full rounded-xl border border-border py-2.5 text-sm font-semibold text-bearish">Cancel Subscription</button>
                )}
                <Link to="/pricing" className="block w-full rounded-xl border border-border py-2.5 text-center text-sm font-semibold">Change plan</Link>
              </div>
            ) : (
              <div className="mt-4 space-y-2">
                <p className="text-xs text-muted-foreground">This plan was granted directly (no Stripe subscription attached). Manage plans below.</p>
                <Link to="/pricing" className="block w-full rounded-xl border border-border py-2.5 text-center text-sm font-semibold">Change plan</Link>
              </div>
            )}
          </div>
          {sub?.transactions?.length > 0 && (
            <div className="rounded-2xl border border-border bg-card p-6">
              <h3 className="font-display text-base font-semibold">Billing history</h3>
              <div className="mt-3 space-y-2 text-sm">
                {sub.transactions.slice(0, 6).map((tx) => (
                  <div key={tx.session_id} className="flex items-center justify-between">
                    <span className="text-muted-foreground">{tx.lookup_key}</span>
                    <span className={`font-mono ${tx.payment_status === "paid" ? "text-bullish" : "text-muted-foreground"}`}>{tx.payment_status}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
}

function Info({ label, value }) {
  return <div><div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div><div className="mt-0.5 font-medium capitalize">{value}</div></div>;
}

function Row2({ label, value, accent }) {
  return <div className="flex items-center justify-between"><span className="text-muted-foreground">{label}</span><span className={`font-medium capitalize ${accent || ""}`}>{value}</span></div>;
}
